import { Instance, types, flow } from "mobx-state-tree";
import { currentUserStoreGetter, servicesGetters } from "../utils";
import { MessageTag } from "@fidget/common/models/messageTag";
import { logger } from "@fidget/common/utils";
import { staticConfig } from "@fidget/common/config/static-config";

interface CobrowsingAcceptationContext {
  sessionId: string;
}

export const CobrowsingStore = types
  .model("CobrowsingStore", {
    cobrowsingAccepted: types.maybe(types.boolean),
    cobrowsingSessionId: types.maybe(types.string),
    cobrowseJwt: types.maybe(types.string),
    cobrowsingInProgress: types.boolean,
    startTimestamp: types.maybe(types.number)
  })
  .views(servicesGetters)
  .views(currentUserStoreGetter)
  .actions((self) => {
    const sendCobrowsingEndedMessage = flow(function* (roomId: string) {
      yield self.artichokeService.sendCustom(roomId, "", MessageTag.COBROWSING_ENDED, {});
    });

    const indicateSessionStart = flow(function* () {
      const timestamp = Date.now();
      self.startTimestamp = timestamp;
      const roomId = self.cobrowsingSessionId;
      if (roomId) {
        yield self.spinnerService.indicateCobrowsingStart(timestamp, roomId);
      }
    });

    const indicateSessionEnd = flow(function* (timestamp: number, roomId: string) {
      const now = Date.now();
      const duration = now - timestamp;
      yield self.spinnerService.indicateCobrowsingEnd(duration, roomId);
    });

    const endCobrowsingSession = (roomId?: string) => {
      if (roomId) {
        indicateSessionEnd(self.startTimestamp || 0, roomId);
      }
      self.cobrowsingAccepted = false;
      self.cobrowsingSessionId = undefined;
      self.cobrowsingInProgress = false;
    };

    const loadJwt = flow(function* () {
      try {
        const { jwt } = yield self.spinnerService.getCobrowseJwt();
        self.cobrowseJwt = jwt;
      } catch (e) {
        logger.error({ message: "Couldn't get cobrowse jwt", error: e });
        const { message }: { message?: string } = yield (e as Response).json();
        throw Error(message);
      }
    });

    const setCobrowsingSession = (sessionId: string) => {
      if (!self.cobrowsingAccepted) {
        self.cobrowsingAccepted = true;
        self.cobrowsingSessionId = sessionId;
      }
    };

    const acceptCobrowsingSession = () => {
      self.cobrowsingInProgress = true;
    };

    return {
      endCobrowsingSession,
      setCobrowsingSession,
      loadJwt,
      acceptCobrowsingSession,
      sendCobrowsingEndedMessage,
      indicateSessionStart
    };
  })
  .views((self) => ({
    get cobrowsingSessionAccepted(): boolean | undefined {
      return self.cobrowsingAccepted;
    },

    get cobrowsingSessionInProgress(): boolean | undefined {
      return self.cobrowsingAccepted && self.cobrowsingInProgress;
    },

    get cobrowsingSessionUrl(): string | undefined {
      return (
        self.cobrowseJwt &&
        self.cobrowsingSessionId &&
        staticConfig.cobrowseIOconnectUrl + self.cobrowsingSessionId + "&token=" + self.cobrowseJwt
      );
    },

    get sessionStartTimestamp(): number | undefined {
      return self.startTimestamp;
    }
  }))
  .actions((self) => {
    const eventListenerName = "CobrowsingListener";
    const afterCreate = () => {
      self.eventListener.addGlobalListener({
        name: eventListenerName,
        customMessage: (event) => {
          if (event.subtag === MessageTag.COBROWSING_ACCEPTATION) {
            const { context }: { context: CobrowsingAcceptationContext } = event;
            self.loadJwt();
            self.setCobrowsingSession(context.sessionId);
          } else if (event.subtag === MessageTag.COBROWSING_ENDED) {
            self.endCobrowsingSession(event.roomId);
          }
        }
      });
    };

    const beforeDestroy = function () {
      self.eventListener.removeGlobalListener(eventListenerName);
    };

    return { afterCreate, beforeDestroy };
  });

export interface ICobrowsingStore extends Instance<typeof CobrowsingStore> {}
